import {Platform, StyleProp, StyleSheet, ViewStyle} from 'react-native';
import {createTheme} from '@rneui/themed';

import {fontFamilyStyle} from '../components/ui-controls/Text';
import config from '../config';
import legacyTheme from '../style/theme';

import {darkenHex} from './colorHelpers';

const rewardsTheme = createTheme({
  // Previous to the rewards project a theme object was being
  // used as a sort of ad hoc theming mechanism we incorporate here
  // so we can theme components as expected
  // includes colour, and size keys
  ...legacyTheme,
  components: {
    Icon(props, theme) {
      return {
        color: theme.colors?.legacy?.text?.heading?.primary,
      };
    },
    CheckBox(props, theme) {
      return theme.colors.primaryDarker
        ? {
            uncheckedColor: theme.colors?.legacy?.uiControls?.primary,
            checkedColor: theme.colors?.legacy?.uiControls?.primary,
          }
        : {};
    },
    Card(
      props: {
        gap?: number;
        noMargin?: boolean;
        variant?: 'outlined' | 'button' | 'button2' | 'information';
        padding?: 'horizontalList';
        radius?: 'rounded-lg' | 'rounded-md';
        applied?: boolean; // use with button2
      },
      theme,
    ) {
      const newProps: {
        containerStyle: StyleProp<ViewStyle>;
        wrapperStyle?: StyleProp<ViewStyle>;
      } = props?.noMargin
        ? {
            containerStyle: {
              marginHorizontal: 0,
              marginVertical: 0,
            },
          }
        : {containerStyle: {}};

      if (props?.variant === 'outlined') {
        newProps.containerStyle = {
          ...newProps.containerStyle,
          borderColor: theme.colors.greyOutline,
          borderRadius: 5,
          padding: 8,
        };
      }

      if (props?.variant === 'button') {
        newProps.containerStyle = {
          ...newProps.containerStyle,
          borderColor: theme.colors.greyOutline,
          borderRadius: 10,
          padding: 10,
          shadowColor: '#000',
          shadowOffset: {
            width: 2,
            height: 2,
          },
          shadowOpacity: 0.25,
          shadowRadius: 0,
          marginRight: 2, // create space for shadow
          marginBottom: 2, // create space for shadow
        };
      }

      if (props?.variant === 'button2') {
        newProps.containerStyle = {
          ...newProps.containerStyle,
          borderRadius: 10,
          paddingLeft: 12,
          paddingRight: 18,
          paddingVertical: 10,
          ...Platform.select({
            web: {
              marginTop: 5, // create space for shadow
              marginBottom: 16, // create space for shadow
            },
            native: {
              marginTop: 20, // create space for shadow
              marginBottom: 24, // create space for shadow
            },
          }),

          ...(props?.applied
            ? {
                backgroundColor: theme.colors.backgroundColorApplied,
                borderColor: theme.colors.greyOutline,
                borderWidth: 1,
              }
            : {
                shadowColor: '#000',
                shadowOffset: {
                  width: 0,
                  height: 4,
                },
                shadowOpacity: 0.25,
                shadowRadius: 10,
              }),
        };
      }

      if (props?.variant === 'information') {
        newProps.containerStyle = {
          ...newProps.containerStyle,
          backgroundColor: theme.colors.grey5,
          padding: 10,
        };
      }

      if (props?.radius === 'rounded-lg') {
        newProps.containerStyle = {
          ...newProps.containerStyle,
          borderRadius: 8,
        };
      }

      if (props?.radius === 'rounded-md') {
        newProps.containerStyle = {
          ...newProps.containerStyle,
          borderRadius: 5,
        };
      }

      if (props?.gap) {
        newProps.wrapperStyle = {
          gap: props.gap,
        };
      }

      if (props?.padding === 'horizontalList') {
        newProps.containerStyle = {
          ...newProps.containerStyle,
          marginLeft: 0,
          marginRight: 8,
          marginVertical: 0,
        };
      }

      return newProps;
    },
    ActivityIndicator(props, theme) {
      return {
        style: {flex: 1},
        size: 'small',
        color: theme.colors?.legacy?.uiControls?.primary,
      };
    },
    WeakPassword(props, theme) {
      return {
        containerStyle: {
          backgroundColor: theme.colors.warning,
        },
        iconColor: '#FF8A00',
      };
    },
    ValidationMessage: {
      style: {
        fontSize: 14,
        marginTop: 8,
        marginBottom: 5,
      },
    },
    ListItemTitle() {
      return {
        style: {
          ...fontFamilyStyle,
        },
      };
    },
    ListItemSubtitle() {
      return {
        style: {
          ...fontFamilyStyle,
        },
      };
    },
    ListItemChevron: {
      type: 'material-design',
      name: 'chevron-right',
    },
    LMGButton(props, theme) {
      const isOutlinedOrText = ['outlined', 'text'].includes(props.variant!);
      const isSecondary = props.secondary;

      // Variant-aware disabled styling
      const disabledStyles =
        isOutlinedOrText || isSecondary
          ? {
              // Outlined/text/secondary: transparent bg, no border, grey text
              disabledStyle: {
                borderWidth: 0,
                backgroundColor: 'transparent',
              },
              disabledTitleStyle: {
                color: theme.colors.grey3,
              },
            }
          : {
              // Solid primary: grey background, darker grey text
              disabledStyle: {
                backgroundColor: theme.colors.legacy.background.button.disabled,
              },
              disabledTitleStyle: {
                color: theme.colors.legacy.text.button.disabled,
              },
            };

      return {
        ...disabledStyles,
        // Only add icon styling if component actually has an icon prop
        // Otherwise RNE renders an empty Icon with android_ripple which
        // causes nested <button> hydration errors on web
        ...(isOutlinedOrText && props.icon
          ? {
              icon: {
                iconStyle: {
                  color: theme.colors.legacy.uiControls.primary,
                },
              },
            }
          : {}),
        buttonStyle: {
          width: props?.width || '100%',
          padding: theme?.size?.s3,
          backgroundColor:
            isOutlinedOrText || isSecondary
              ? theme.colors.white
              : props.color === 'error'
                ? theme.colors.error
                : theme.colors.legacy.uiControls.primary,
          borderRadius: theme?.size?.s1,
          borderColor:
            props.color === 'error' ? theme.colors.error : theme.colors.legacy.uiControls.primary,
          borderWidth: ['outlined'].includes(props.variant!) || isSecondary ? 1 : 0,
        },
        titleStyle: {
          ...fontFamilyStyle,
          fontWeight: '700',
          color:
            isOutlinedOrText || isSecondary
              ? props.color === 'error'
                ? theme.colors.error
                : theme.colors?.legacy?.uiControls?.primary
              : theme.colors.white,
        },
      };
    },
    LMGText(props: {color?: string}, theme) {
      const newProps =
        props.variant === 'heading1'
          ? {
              style: {
                color: theme.colors?.legacy?.text?.heading?.primary,
                marginTop: theme?.size?.s5,
                marginBottom: theme.size?.s5,
                // fontWeight: 600,
              },
            }
          : props.variant === 'subheading'
            ? {
                style: {
                  ...fontFamilyStyle,
                  color: theme.colors?.legacy?.text?.heading?.primary,
                  fontSize: theme?.size?.text?.heading2 ?? 18,
                  fontWeight: '600',
                  marginTop: theme?.size?.s5,
                  marginBottom: theme?.size?.s2,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                },
              }
            : {
                style: {
                  // lineHeight: '150%',
                },
              };

      if (props?.noMargin) {
        newProps.style = {
          ...(newProps.style || {}),
          marginTop: 0,
          marginBottom: 0,
        };
      }

      if (props?.color) {
        newProps.style = {
          ...(newProps.style || {}),
          color: theme.colors?.[props?.color] || props?.color,
        };
      }

      if (props?.center) {
        newProps.style = {
          ...(newProps.style || {}),
          textAlign: 'center',
        };
      }

      return newProps;
    },
    KeyboardAwareScrollView: (props, theme) => {
      return {
        enableOnAndroid: true,
        extraScrollHeight: 40,
        style: {
          backgroundColor: theme.colors.background,
        },
      };
    },
    TextInput: (props, theme) => {
      return StyleSheet.create({
        containerStyle: {
          paddingHorizontal: 0,
          //   width: '50%',
        },
        inputStyle: {
          ...fontFamilyStyle,
          fontSize: props?.largeText ? 22 : theme?.size?.text?.body2,
          fontWeight: props?.largeText ? '500' : '400',
          borderRadius: props?.largeText ? theme?.size?.s2 : theme?.size?.s1,
          paddingHorizontal: theme?.size?.s4,
          paddingVertical: theme?.size?.s3,
          borderColor: theme.colors.grey3 ?? '#D1D5DB',
          color: theme.colors.black ?? '#111928',
          // backgroundColor: '#F9FAFB',
          borderWidth: 1,
          ...Platform.select({
            web: {
              outlineWidth: 0,
            },
          }),
        },
        inputFocusStyle: {
          // borderColor: '#1C64F2',
          // borderWidth: 1,
        },
        disabledInputStyle: {
          backgroundColor: '#F3F3F3',
          borderColor: '#9CA3AF',
          color: '#7A7A7A',
        },
        inputContainerStyle: {
          borderBottomWidth: 0,
        },
        labelStyle: {
          ...fontFamilyStyle,
          color: theme.colors.black,
          marginBottom: theme?.size?.s2,
          fontWeight: '400',
        },
        errorStyle: {
          ...fontFamilyStyle,
          fontSize: theme?.size?.text?.caption,
          marginHorizontal: 0,
          marginTop: 8,
        },
      });
    },
    DateOfBirthInput: (props, theme) => {
      return {
        style: {
          paddingHorizontal: 0,
        },
        dropDownContainerStyle: {
          borderColor: '#D1D5DB',
        },
        dropDownStyle: {
          borderColor: '#D1D5DB',
        },
        textStyle: {
          fontSize: theme?.size?.text?.body2,
        },
      };
    },
  },
  mode: 'light',
  lightColors: {
    yellow: legacyTheme.colour.background.memberPriceBadge,
    primary: legacyTheme.colour.uiControls.primary,
    primaryBrighter:
      config.APP_NAME === 'Bottlemart' ? '#1A56DB' : legacyTheme.colour.general.primary,
    primaryDarker:
      config.APP_NAME === 'Bottlemart' ? '#0054A6' : legacyTheme.colour.general.primary,
    primaryDarkest:
      config.APP_NAME === 'Bottlemart'
        ? '#003E70'
        : darkenHex(legacyTheme.colour.general.primary, 0.04),
    black: '#111928',
    warning: '#FFF4DE',
    error: '#C81E1E',
    grey0: '#373737',
    grey1: '#5B5B5B',
    grey2: '#7E7E7E',
    grey3: '#A4A4A4',
    // grey4: '#A4A4A4', // we don't have color for this
    grey5: '#F3F3F3',
    greyOutline: 'rgba(0,0,0,0.12)',

    // greyOutline: 'rgb(128, 128, 128)',
    backgroundColorSelected: '#D7E7FF',
    backgroundColorApplied: '#F6FBFF',

    link: '#1C64F2', // light blue

    // --- DIS-1186 semantic surface + text + link tokens ---
    // Use these in place of raw hex in rewards screens.
    surfaceSubtle: '#F9FAFB',
    surfaceWarning: '#FEF3C7',
    borderSubtle: '#E5E7EB',
    textMuted: '#6B7280',
    textWarning: '#92400E',
    textOnSurfaceStrong: '#374151',
    linkPrimary: '#1A56DB',
    linkOnSurface: '#0054A6',
    hintMuted: '#6F6F6F',

    // all legacy colours here
    legacy: legacyTheme.colour,
    ...legacyTheme.colour,
  },
  size: {
    text: {
      ...legacyTheme.size.text,
    },
    s0: 0,
    s1: 4,
    s2: 8,
    s3: 12,
    s4: 16,
    s5: 20,
    s6: 24,
    s7: 30,
    s8: 36,
  },
});

export default rewardsTheme;
